<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useProjectionsStore } from '../stores/projections'
import { useSplitViewStore } from '../stores/splitViewStore'
import PlayerDetailsSidebar from './PlayerDetailsSidebar.vue'
import PlayerStatsSummary from './PlayerStatsSummary.vue'
import PlayerGameStatsTable from './PlayerGameStatsTable.vue'
import axios from '../axios'
import { apiBalancedProjectionsPath } from '../config/balancedProjectionsApi.js'

const props = defineProps({
  playerId: {
    type: [String, Number],
    required: true
  },
  show: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close'])
const projectionsStore = useProjectionsStore()
const splitViewStore = useSplitViewStore()
const playerDetails = ref(null)
const loading = ref(false)
const rows = ref([]) // Make rows reactive

// Add filtered games state
const filteredGames = ref([])

// Store calculated stats from PlayerStatsSummary
const calculatedStats = ref(null)

// Split view mode
const isSplitMode = computed(() => splitViewStore.isSplitMode)

// Get data from projections store
const projectionsData = computed(() => projectionsStore.projectionsData)

// Get current player data from projections store
const currentPlayerData = computed(() => {
  if (!projectionsData.value || !projectionsData.value.fixture_projections || !Array.isArray(projectionsData.value.fixture_projections)) {
    console.log('No fixture_projections data available:', projectionsData.value)
    return null
  }
  
  const player = projectionsData.value.fixture_projections.find(p => p.player_id == props.playerId)
  
  if (player) {
    // console.log('Found player data in store:', {
    //   playerId: props.playerId,
    //   player: player,
    //   seasons: player.seasons,
    //   selected_leagues: player.selected_leagues
    // })
  } else {
    console.log('Player not found in store. Available players:', 
      projectionsData.value.fixture_projections.map(p => ({
        player_id: p.player_id,
        seasons: p.seasons,
        selected_leagues: p.selected_leagues
      }))
    )
  }
  
  return player
})

// Available seasons and leagues from the store's projection data
const seasons = [
  '2025-2026', '2024-2025', '2023-2024', '2022-2023', '2021-2022', '2020-2021', '2019-2020','2018-2019', '2017-2018'
]

// Initialize with store filters for this modal instance
const selectedSeason = ref([])
const selectedLeagues = ref([])
const selectedAdditionalFilter = ref('allGames')

// Get available leagues from store
// Utility function to sort an array of objects by name A-Z
function sortByAZ(arr) {
  // Defensive: don't mutate original
  return [...arr].sort((a, b) => a.name.localeCompare(b.name));
}

const availableLeagues = computed(() => {
  const LEAGUES = [
    { id: 'friendly-international', name: 'Friendly International' },
    { id: 'adriatic-aba-liga-2', name: 'Adriatic ABA Liga 2' },
    { id: 'adriatic-liga-aba', name: 'Adriatic Liga ABA' },
    { id: 'adriatic-liga-aba-playoffs', name: 'Adriatic Liga ABA Playoffs' },
    { id: 'argentina-la-liga-torneo-super-20', name: 'Argentina La Liga Torneo Super 20' },
    { id: 'argentina-liga-a', name: 'Argentina Liga A' },
    { id: 'australia-nbl', name: 'Australia NBL' },
    { id: 'austria-superliga', name: 'Austria Superliga' },
    { id: 'basketball-champions-league', name: 'Basketball Champions League' },
    { id: 'basketball-champions-league-americas', name: 'Basketball Champions League Americas' },
    { id: 'bnxt-league', name: 'BNXT League' },
    { id: 'bosnia-division-i', name: 'Bosnia Division I' },
    { id: 'bulgaria-nbl', name: 'Bulgaria NBL' },
    { id: 'china-cba', name: 'China CBA' },
    // { id: 'croatia-liga', name: 'Croatia Liga' },
    { id: 'czech-republic-mattoni-nbl', name: 'Czech Republic Mattoni NBL' },
    { id: 'denmark-ligaen', name: 'Denmark Ligaen' },
    { id: 'eurocup', name: 'EuroCup' },
    { id: 'euroleague', name: 'EuroLeague' },
    { id: 'fiba-europe-cup', name: 'FIBA Europe Cup' },
    { id: 'finland-korisliiga', name: 'Finland Korisliiga' },
    { id: 'france-betclic-elite', name: 'France Betclic Elite' },
    { id: 'france-betclic-elite-playoffs', name: 'France Betclic Elite Playoffs' },
    { id: 'france-elite-2', name: 'France Elite 2' },
    { id: 'georgia-superleague', name: 'Georgia Superleague' },
    { id: 'germany-easycredit-bbl', name: 'Germany EasyCredit BBL' },
    { id: 'germany-pro-a', name: 'Germany Pro A' },
    { id: 'greece-heba-a1', name: 'Greece HEBA A1' },
    { id: 'greece-heba-a2', name: 'Greece HEBA A2' },
    { id: 'hungary-a-league', name: 'Hungary A League' },
    { id: 'iceland-express-league', name: 'Iceland Express League' },
    { id: 'israel-winner-league', name: 'Israel Winner League' },
    { id: 'italy-lba-serie-a', name: 'Italy LBA Serie A' },
    { id: 'italy-serie-a2', name: 'Italy Serie A2' },
    { id: 'japan-b1-league', name: 'Japan B1 League' },
    { id: 'japan-b2-league', name: 'Japan B2 League' },
    { id: 'latvian-estonian-basketball-league', name: 'Latvian-Estonian Basketball League' },
    { id: 'lithuania-betsafe-lkl', name: 'Lithuania Betsafe LKL' },
    { id: 'lithuania-betsafe-lkl-playoffs', name: 'Lithuania Betsafe LKL Playoffs' },
    { id: 'lithuania-nkl', name: 'Lithuania NKL' },
    { id: 'mexico-liga-sisnova-lnbp', name: 'Mexico Liga Sisnova LNBP' },
    { id: 'nba', name: 'NBA' },
    { id: 'ncaa', name: 'NCAA' },
    { id: 'new-zealand-nbl', name: 'New Zealand NBL' },
    { id: 'norway-blno', name: 'Norway BLNO' },
    { id: 'poland-plk', name: 'Poland PLK' },
    { id: 'portugal-liga-profissional', name: 'Portugal Liga Profissional' },
    { id: 'puerto-rico-bsn', name: 'Puerto Rico BSN' },
    { id: 'romania-division-a', name: 'Romania Division A' },
    { id: 'serbia-kls', name: 'Serbia KLS' },
    { id: 'slovakia-extraliga', name: 'Slovakia Extraliga' },
    { id: 'slovenia-liga-nova-kbm', name: 'Slovenia Liga Nova KBM' },
    { id: 'spain-leb-gold', name: 'Spain LEB Gold' },
    { id: 'spain-liga-endesa', name: 'Spain Liga Endesa' },
    { id: 'super-league-basketball', name: 'Super League Basketball' },
    { id: 'sweden-basketligan', name: 'Sweden Basketligan' },
    { id: 'switzerland-sbl', name: 'Switzerland SBL' },
    { id: 'turkey-bsl', name: 'Turkey BSL' },
    { id: 'turkey-tbl', name: 'Turkey TBL' },
    { id: 'vtb-united-league', name: 'VTB United League' },
    // Added international and tournament leagues
    { id: 'world-cup', name: 'World Cup' },
    { id: 'eurobasket', name: 'EuroBasket' },
    { id: 'eurobasket-2025', name: 'EuroBasket 2025' },
    { id: 'world-cup-european-qualifiers', name: 'World Cup European Qualifiers' },
    { id: 'eurobasket-qualifying-rounds', name: 'EuroBasket Qualifying Rounds' },
    { id: 'olympic-games', name: 'Olympic Games' },
    { id: 'olympic-games-qualifying-tournament', name: 'Olympic Games Qualifying Tournament' },
    { id: 'preparation', name: 'Preparation' },
    { id: 'cyprus-division-a', name: 'Cyprus Division A' },
    { id: 'basketball-champions-league-qualifying-rounds', name: 'Basketball Champions League Qualifying Rounds' },
    { id: 'fiba-europe-cup-qualifying-rounds', name: 'FIBA Europe Cup Qualifying Rounds' },
    { id: 'croatia-liga', name: 'Croatia Liga' },
    { id: 'czech-republic-mattoni-nbl', name: 'Czech Republic Mattoni NBL' },
  { id: 'enbl', name: 'ENBL' },
  { id: 'brazil-nbb', name: 'Brazil NBB' },
  { id: 'brazilian-nbb', name: 'Brazilian NBB' },
  { id: 'argentina-liga-a', name: 'Argentina Liga A' },
  { id: 'king-mindaugas-cup-play-offs', name: 'King Mindaugas Cup Play Off' },
  { id: 'latvian-cup', name: 'Latvian Cup' },
  { id: 'enbl', name: 'ENBL' },
  { id: 'turkish-cup', name: 'Turkish Cup' },
  { id: 'italian-cup', name: 'Italian Cup' },
  { id: 'spanish-cup', name: 'Spanish Cup' },
  { id: 'world-cup-european-pre-qualifiers', name: 'World Cup European Pre Qualifiers' },
  ]
  return sortByAZ(LEAGUES);
})

const stats = [
  { label: 'Points', projected: '0.00', per30: '13.93' },
  { label: 'Rebounds', projected: '0.00', per30: '4.47' },
  { label: 'Assists', projected: '0.00', per30: '7.91' },
  { label: '3PT', projected: '0.00', per30: '1.48' },
  { label: 'PRA', projected: '0.00', per30: '30.84' },
  { label: 'Turnovers', projected: '0.00', per30: '3.98' },
  { label: 'Blocks', projected: '0.00', per30: '0.04' },
  { label: 'Steals', projected: '0.00', per30: '1.48' }
]
const columns = [
  { key: 'game_date', label: 'Date' },
  { key: 'game_id', label: 'Game ID' },
  { key: 'player_name', label: 'Player' },
  { key: 'player_id', label: 'Player ID' },
  { key: 'league_name', label: 'League' },  
  { key: 'season', label: 'Season' },
  { key: 'team', label: 'Team' },
  { key: 'opponent_team_name', label: 'Opponent' },
  { key: 'home_team_score', label: 'Home PTS' },
  { key: 'away_team_score', label: 'Away PTS' },
  { key: 'minutes', label: 'Min' },
  { key: 'points', label: 'PTS' },
  { key: 'rebounds', label: 'REB' },
  { key: 'assists', label: 'AST' },
  { key: 'three_point_field_goal', label: '3PM' },
  { key: 'steals', label: 'STL' },
  { key: 'blocks', label: 'BLK' },
  { key: 'turnovers', label: 'TO' },
  { key: 'pra', label: 'PRA' },
  { key: 'efficiency', label: 'EFF' }
]

// Example: Watch for changes and fetch games
watch([selectedLeagues, selectedSeason, selectedAdditionalFilter], async ([leagues, seasons, additionalFilter]) => {
  if (projectionsStore.projectionsData?.fixture_projections) {
    // Find the projection for the current player safely
    const playerProjection = projectionsStore.projectionsData.fixture_projections
      ? projectionsStore.projectionsData.fixture_projections.find(
          p => p && p.player_id == props.playerId
        )
      : null;
    if (playerProjection) {
      playerProjection.selected_leagues = selectedLeagues.value;
      playerProjection.seasons = selectedSeason.value;
    }
    // Always call fetchGames with current values
    await fetchGames(selectedLeagues.value, selectedSeason.value, additionalFilter);
  } else {
    // If fixture_projections is missing, still fetch
    await fetchGames(selectedLeagues.value, selectedSeason.value, additionalFilter);
  }
}, { deep: true })

async function fetchGames(leagues, seasons, additionalFilter = 'allGames') {
  // console.log('Fetching games with:', {
  //   playerId: props.playerId,
  //   seasons: seasons,
  //   leagues: leagues,
  //   additionalFilter: additionalFilter
  // })
  loading.value = true
  try {
    // Prepare POST request body
    const requestBody = {
      player_id: props.playerId,
      seasons: seasons.length > 0 ? seasons : [],
      leagues: leagues.length > 0 ? leagues : [],
      additional_filter: additionalFilter,
      fixtureId: projectionsStore.projectionsData.id
    }

    // Make the API call using POST method
    const response = await axios.post('/api/players/games', requestBody)
    
    // With axios, the data is already parsed and available at response.data
    let data = response.data
    
    // console.log('API Response:', data)
    
    // Handle different possible response structures
    if (Array.isArray(data)) {
      // If data is directly an array
      data = data
    } else if (data && Array.isArray(data.games)) {
      // If data has a games property with array
      data = data.games
    } else if (data && Array.isArray(data.data)) {
      // If data has a data property with array
      data = data.data
    } else {
      // Convert object with numeric keys to array
      const gameArray = Object.values(data).filter(item => typeof item === 'object' && item.id)
      data = gameArray
    }
    
    // Apply additional filtering based on the selected option
    data = applyAdditionalFilter(data, additionalFilter)
    
    // Update rows with filtered data
    rows.value = data
    
    // console.log('Games loaded:', rows.value.length, 'games')
    
  } catch (error) {
    console.error('Error fetching games:', error)
    // Optionally show user-friendly error message
    rows.value = []
  } finally {
    loading.value = false
  }
}

// Function to apply additional filtering based on the selected option
function applyAdditionalFilter(games, filterType) {
  if (!games || games.length === 0) return games
  
  // console.log(`Applying ${filterType} filter to ${games.length} games`)
  
  switch (filterType) {
    case 'lastFiveGames':
      // Return only the last 5 games (most recent by date)
      const sortedGames = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      const lastFive = sortedGames.slice(0, 5)
      // console.log(`Last 5 games filter: returning ${lastFive.length} games`)
      return lastFive
      
    case 'restOfSeason':
      // Return all games except the last 5
      const sortedGamesForRest = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      const restOfSeason = sortedGamesForRest.slice(5)
      // console.log(`Rest of season filter: returning ${restOfSeason.length} games`)
      return restOfSeason
      
    case 'allGames':
    default:
      // Return all games
      // console.log(`All games filter: returning ${games.length} games`)
      return games
  }
}

onMounted(async () => {
  try {
    // Initialize filters from store's projections data for this specific player
    if (currentPlayerData.value) {
      // Use the player's specific filter data from projectionStore.projectionsData.fixture_projections
      selectedSeason.value = Array.isArray(currentPlayerData.value.seasons) 
        ? [...currentPlayerData.value.seasons] 
        : []
      selectedLeagues.value = Array.isArray(currentPlayerData.value.selected_leagues) 
        ? [...currentPlayerData.value.selected_leagues] 
        : []
      
      // console.log('Player filter data from store:', {
      //   playerId: props.playerId,
      //   seasons: selectedSeason.value,
      //   leagues: selectedLeagues.value,
      //   playerData: currentPlayerData.value
      // })
    } else {
      // Fallback to store's global filters if player data not found
      selectedSeason.value = [...projectionsStore.getFilterSeasons]
      selectedLeagues.value = [...projectionsStore.getFilterLeagues]
      
      // console.log('Using fallback global filters:', {
      //   playerId: props.playerId,
      //   seasons: selectedSeason.value,
      //   leagues: selectedLeagues.value
      // })
    }
    
    // Initial load with selected values
    if (selectedSeason.value.length > 0 || selectedLeagues.value.length > 0) {
      await fetchGames(selectedLeagues.value, selectedSeason.value, selectedAdditionalFilter.value)
    } else {
      // console.log('No filters selected, skipping initial fetch')
      loading.value = false
    }
    
    // TODO: Replace with actual player details API call if needed
    // const response = await fetch(`/api/players/${props.playerId}`)
    // playerDetails.value = await response.json()
    
  } catch (error) {
    console.error('Error fetching initial data:', error)
    loading.value = false
  }
})

// Watch rows and initialize filteredGames
watch(rows, (newRows) => {
  // Initialize filtered games with all games when rows change
  if (newRows && newRows.length > 0) {
    filteredGames.value = [...newRows]
  }
}, { immediate: true })

function closeModal() {
  emit('close')
}

// Handle filtered stats from table
function handleStatsUpdated(filteredStats) {
  // Update filtered games for stats summary
  // The filteredStats object contains the filtered rows data
  // console.log('Filtered stats updated:', filteredStats)
}

// Handle filtered rows from table  
function handleFilteredRowsUpdated(filteredRowsData) {
  filteredGames.value = filteredRowsData
  // console.log('Filtered games updated:', filteredRowsData.length, 'games')
}

// Handle calculated stats from PlayerStatsSummary
async function handleStatsCalculated(stats) {
  calculatedStats.value = stats
  
  // Emit complete filter data including stats
  const filterData = {
    fixtureId: projectionsStore.projectionsData.id || 3,
    player_id: props.playerId,
    leagues: selectedLeagues.value,
    seasons: selectedSeason.value,
    filteredGames: filteredGames.value.length,
    calculatedStats: stats,
    // Include totals and per30 for easy access
    totals: {
      points: stats.stats.points?.total || 0,
      rebounds: stats.stats.rebounds?.total || 0,
      assists: stats.stats.assists?.total || 0,
      three_pointers: stats.stats['3pt']?.total || 0,
      pra: stats.stats.pra?.total || 0,
      turnovers: stats.stats.turnovers?.total || 0,
      blocks: stats.stats.blocks?.total || 0,
      steals: stats.stats.steals?.total || 0
    },
    per30: {
      points: stats.stats.points?.per30 || 0,
      rebounds: stats.stats.rebounds?.per30 || 0,
      assists: stats.stats.assists?.per30 || 0,
      three_pointers: stats.stats['3pt']?.per30 || 0,
      pra: stats.stats.pra?.per30 || 0,
      turnovers: stats.stats.turnovers?.per30 || 0,
      blocks: stats.stats.blocks?.per30 || 0,
      steals: stats.stats.steals?.per30 || 0
    },
    projected: {
      points: stats.stats.points?.projected || 0,
      rebounds: stats.stats.rebounds?.projected || 0,
      assists: stats.stats.assists?.projected || 0,
      three_pointers: stats.stats['3pt']?.projected || 0,
      pra: stats.stats.pra?.projected || 0,
      turnovers: stats.stats.turnovers?.projected || 0,
      blocks: stats.stats.blocks?.projected || 0,
      steals: stats.stats.steals?.projected || 0
    }
  }
  
  // console.log('Complete filter data with stats:', filterData)
  
  // Send calculated stats to API
  try {
    const response = await axios.post(apiBalancedProjectionsPath('projections/stats-update'), filterData)
    // let playerProjection = projectionsStore.projectionsData.fixture_projections.find(p => p.player_id === response.data.player_id)
    const arr = projectionsStore.projectionsData.fixture_projections;
    const idx = arr.findIndex(p => p.player_id === response.data.player_id);
    if (idx !== -1) {
      // Vue 3's proxy-based reactivity will pick up direct index assignment
      arr[idx] = response.data;
    }
    // console.log('Player projection updated:', arr) 
    // console.log('Stats sent successfully:', response.data)
    // console.log('Player projection updated:', projectionsStore.projectionsData.fixture_projections.find(p => p.player_id === props.playerId))
  } catch (error) {
    console.error('Error sending calculated stats:', error)
  }
  
  // You can also emit this data to parent component
  // emit('filter-data-updated', filterData)
}

// Toggle split mode
function toggleSplitMode() {
  if (isSplitMode.value) {
    splitViewStore.disableSplitMode()
  } else {
    splitViewStore.enableSplitMode()
  }
}

// Watch for split mode changes to close modal when exiting split mode
watch(isSplitMode, (newValue) => {
  if (!newValue) {
    // Optional: close modal when exiting split mode
    // emit('close')
  }
})
</script>

<template>
  <!-- Normal Modal Mode -->
  <div v-if="show && !isSplitMode" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="emit('close')">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <div class="relative w-[95vw] h-[90vh] bg-white rounded-lg shadow-xl flex overflow-hidden">
        <!-- Modal Content -->
        <div class="w-full h-full flex">
          <!-- Sidebar -->
          <PlayerDetailsSidebar
            :seasons="seasons"
            :selected-season="projectionsStore.projectionsData?.fixture_projections?.find(p => p.player_id == props.playerId)?.seasons"
            :selected-leagues="projectionsStore.projectionsData?.fixture_projections?.find(p => p.player_id == props.playerId)?.selected_leagues"
            :available-leagues="availableLeagues"
            @update:selectedSeason="val => selectedSeason = val"
            @update:selectedLeagues="val => selectedLeagues = val"
            @update:additionalFilter="val => selectedAdditionalFilter = val"
          />
          
          <!-- Main Content -->
          <div class="flex-1 flex flex-col bg-gray-50 h-full min-h-0">
            <!-- Header -->
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 class="text-xl font-semibold text-gray-900">
                {{ currentPlayerData?.player?.player_name || 'Player' }} Game Stats
                <span v-if="selectedSeason.length" class="text-sm text-gray-500 ml-2">
                  ({{ selectedSeason.join(', ') }})
                </span>
              </h3>
              
              <!-- Header Buttons -->
              <div class="flex items-center gap-2">
                <button class="bg-white hover:bg-gray-50 border-2 rounded-lg p-2 transition-all duration-200 shadow-sm border-gray-200 text-gray-500 hover:border-gray-300" @click="toggleAdditionalFilter">Exclude/Include Games with or without selected players</button>


                <!-- Split Mode Button -->
                <button
                  @click="toggleSplitMode"
                  class="bg-white hover:bg-gray-50 border-2 rounded-lg p-2 transition-all duration-200 shadow-sm"
                  :class="isSplitMode ? 'border-blue-500 text-blue-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'"
                  :title="isSplitMode ? 'Exit Split View' : 'Open in Split View'"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path v-if="!isSplitMode" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <!-- Close Button -->
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-500 focus:outline-none bg-white hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200"
                  @click="emit('close')"
                >
                  <span class="sr-only">Close</span>
                  <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Stats Summary -->
            <PlayerStatsSummary 
              :games="filteredGames" 
              :projected-minutes="currentPlayerData?.projected_minutes || 0" 
              @stats-calculated="handleStatsCalculated"
            />
            
            <!-- Table -->
            <div class="flex-1 min-h-0 p-4 flex flex-col">
              <PlayerGameStatsTable 
                :columns="columns" 
                :rows="rows" 
                @stats-updated="handleStatsUpdated" 
                @filtered-rows-updated="handleFilteredRowsUpdated" 
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Split Mode using Teleport -->
  <Teleport v-if="show && isSplitMode" to="#split-panel .overflow-auto">
    <div class="h-full bg-white">
      <!-- Duplicate Modal Content for Split Mode -->
      <div class="w-full h-full flex">
        <!-- Sidebar -->
        <PlayerDetailsSidebar
          :seasons="seasons"
          :selected-season="projectionsStore.projectionsData?.fixture_projections?.find(p => p.player_id == props.playerId)?.seasons"
          :selected-leagues="projectionsStore.projectionsData?.fixture_projections?.find(p => p.player_id == props.playerId)?.selected_leagues"
          :available-leagues="availableLeagues"
          @update:selectedSeason="val => selectedSeason = val"
          @update:selectedLeagues="val => selectedLeagues = val"
          @update:additionalFilter="val => selectedAdditionalFilter = val"
        />
        
        <!-- Main Content -->
        <div class="flex-1 flex flex-col bg-gray-50 h-full min-h-0">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 class="text-xl font-semibold text-gray-900">
              {{ currentPlayerData?.player?.player_name || 'Player' }} Game Stats
              <span v-if="selectedSeason.length" class="text-sm text-gray-500 ml-2">
                ({{ selectedSeason.join(', ') }})
              </span>
            </h3>
            
            <!-- Header Buttons -->
            <div class="flex items-center gap-2">
              <!-- Split Mode Button -->
              <button
                @click="toggleSplitMode"
                class="bg-white hover:bg-gray-50 border-2 rounded-lg p-2 transition-all duration-200 shadow-sm border-blue-500 text-blue-600"
                title="Exit Split View"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <!-- Close Button -->
              <button
                type="button"
                class="text-gray-400 hover:text-gray-500 focus:outline-none bg-white hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200"
                @click="emit('close')"
              >
                <span class="sr-only">Close</span>
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <!-- Stats Summary -->
          <PlayerStatsSummary 
            :games="filteredGames" 
            :projected-minutes="currentPlayerData?.projected_minutes || 0" 
            @stats-calculated="handleStatsCalculated"
          />
          
          <!-- Table -->
          <div class="flex-1 min-h-0 p-4 flex flex-col">
            <PlayerGameStatsTable 
              :columns="columns" 
              :rows="rows" 
              @stats-updated="handleStatsUpdated" 
              @filtered-rows-updated="handleFilteredRowsUpdated" 
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template> 